import { getLanguageOnlyFromLocale } from "@msys/tolgee";
import type { KeycloakLoginOptions } from "keycloak-js";
import {
  ORGANISATION_TYPE_URL_SEARCH_PARAM,
  REF_TOKEN_URL_SEARCH_PARAM,
} from "../../constants.js";
import { SIGNUP_SCOPE } from "./signupScope.js";

/**
 * Keycloak options for the self-service signup started on `/signup`.
 *
 * Pure (no keycloak-js instance, no DOM): the caller hands in the language,
 * the page location and, on web, the app's router basename, and passes the
 * result to `auth.login`.
 */

/** Alias of the Google identity provider in the `meister` realm. */
export const GOOGLE_IDP_HINT = "google";

/** The locales the realm's login theme is translated to. */
const REALM_LOCALES = ["de", "en", "fr", "pl", "nl"] as const;
export type RealmLocale = (typeof REALM_LOCALES)[number];

const DEFAULT_REALM_LOCALE: RealmLocale = "de";

function isRealmLocale(value: string): value is RealmLocale {
  return REALM_LOCALES.some(locale => locale === value);
}

/**
 * Maps the app language (e.g. `en-GB`, `de-RF`) to the realm locale sent as
 * `ui_locales`. A language the realm does not offer falls back to German.
 */
export function toRealmLocale(appLanguage: string): RealmLocale {
  const language = getLanguageOnlyFromLocale(appLanguage).toLowerCase();
  return isRealmLocale(language) ? language : DEFAULT_REALM_LOCALE;
}

/**
 * Maps the app language to the language of the marketing website's pages
 * (terms, privacy). The website only offers German and English, so every
 * language but German gets the English page, like the Keycloak theme does.
 */
export function toWebsiteLocale(appLanguage: string): "de" | "en" {
  return getLanguageOnlyFromLocale(appLanguage).toLowerCase() === "de"
    ? "de"
    : "en";
}

/** The query parameters that must survive the round trip through Keycloak. */
const PRESERVED_URL_SEARCH_PARAMS = [
  ORGANISATION_TYPE_URL_SEARCH_PARAM,
  REF_TOKEN_URL_SEARCH_PARAM,
];

interface SignupRedirectLocation {
  /** `window.location.origin` */
  origin: string;
  /** The router basename (`VITE_ROUTER_BASENAME`), may be empty or carry slashes. */
  basename: string | undefined;
  /** The current page's query string, with or without the leading `?`. */
  search: string;
}

/**
 * The redirect URI of a web signup: the app root, carrying only the
 * organisation type and the referral token of the current page.
 *
 * keycloak-js would otherwise return to the current page (`/signup`), which
 * drops its query on the way into the app. The landing page forwards the
 * root, with its query, to the authenticated app, which is where
 * `AuthenticatedRoutes` reads both parameters.
 */
export function buildSignupRedirectUri({
  origin,
  basename,
  search,
}: SignupRedirectLocation): string {
  const prefix = basenamePrefix(basename);
  const current = new URLSearchParams(search);
  const preserved = new URLSearchParams();
  for (const name of PRESERVED_URL_SEARCH_PARAMS) {
    const value = current.get(name);
    if (value !== null) {
      preserved.set(name, value);
    }
  }
  const query = preserved.toString();
  return `${origin}/${prefix}${query ? `?${query}` : ""}`;
}

/**
 * Where the web logout returns: the welcome page, under the router basename,
 * so a signed-out visitor starts again with Google and the work email.
 */
export function buildSignedOutRedirectUri({
  origin,
  basename,
}: Omit<SignupRedirectLocation, "search">): string {
  return `${origin}/${basenamePrefix(basename)}signup`;
}

/** The router basename as a path prefix: empty, or without slashes plus `/`. */
function basenamePrefix(basename: string | undefined): string {
  const normalizedBasename = basename ? basename.replace(/^\/+|\/+$/g, "") : "";
  return normalizedBasename ? `${normalizedBasename}/` : "";
}

interface SignupAuthOptionsInput {
  /** The current app language (e.g. `en-GB`). */
  language: string;
  /**
   * Overrides keycloak-js' default redirect URI. Leave it out on the native
   * build, where the Capacitor adapter returns through its own deep link.
   */
  redirectUri?: string | undefined;
}

/**
 * Options for "Continue" with a work email (`auth.login`): the identity-first
 * Keycloak login. The email travels as `login_hint`; Keycloak continues an
 * existing account with its sign-in steps (passkey, password or code by email)
 * and sends an unknown email to the registration page with the email
 * prefilled.
 */
export function buildEmailSignupLoginOptions({
  language,
  redirectUri,
  email,
}: SignupAuthOptionsInput & { email: string }): KeycloakLoginOptions {
  return {
    loginHint: email,
    scope: SIGNUP_SCOPE,
    locale: toRealmLocale(language),
    ...(redirectUri ? { redirectUri } : {}),
  };
}

/**
 * Options for "Continue with Google" and for the One Tap hand-over
 * (`auth.login`): Keycloak forwards straight to Google. A `loginHint` is only
 * a hint for Google's account chooser, never an identity claim.
 */
export function buildGoogleSignupLoginOptions({
  language,
  redirectUri,
  loginHint,
}: SignupAuthOptionsInput & {
  loginHint?: string | undefined;
}): KeycloakLoginOptions {
  return {
    idpHint: GOOGLE_IDP_HINT,
    scope: SIGNUP_SCOPE,
    locale: toRealmLocale(language),
    ...(loginHint ? { loginHint } : {}),
    ...(redirectUri ? { redirectUri } : {}),
  };
}
